import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { RouterModule } from '@angular/router';
import { SharedModule } from './shared/shared.module';
import { OverlayModule } from '@angular/cdk/overlay';
import { LoadingBarModule } from '@ngx-loading-bar/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgxsModule } from '@ngxs/store';
import { AuthState } from './_state/auth/auth.state';
import { OrderState } from './_state/order/order.state';
import { ToastrModule } from 'ngx-toastr';
import { TranslocoModule, provideTransloco } from '@jsverse/transloco';
import { AuthModule } from './core/auth/auth.module';
import { AuthShopModule } from './core/auth-shop/auth-shop.module';
import { PlansModule } from './core/plans/plans.module';
import { JwtInterceptor } from './core/auth/interceptors/auth.interceptor';
import { InventoryState } from './_state/inventory/inventory.state';
import { ShopState } from './_state/shop/shop.state';
import { ModuleAccessDirective } from './shared/directive/module-access.directive';
import { CanEditDirective } from './shared/directive/permission-edit.directive';
import { DemoRoutingService } from './core/services/demo-routing.service';
import { BootstrapPreloadService } from './core/services/bootstrap-preload.service';
import { DemoCheckInterceptor } from './core/auth/interceptors/demo-check.interceptor';
import { CollaboratorState } from './_state/collaborator/collaborator.state';
import { MetricsWidgetConfigState } from './_state/metrics-widget-config/metrics-widget-config.state';
import { UserState } from './_state/user/user.state';
import { KbChatbotComponent } from './shared/components/kb-chatbot/kb-chatbot.component';
import { TranslocoHttpLoader } from './core/i18n/transloco-loader';
import { environment } from '../environments/environment';

// Фабрична функція для APP_INITIALIZER
export function initDemoRouting(demoRoutingService: DemoRoutingService) {
  return () => demoRoutingService.initDemoRouting();
}

// dashboardly-rucz3 Phase-B+: fire-and-forget APP_INITIALIZER — starts the authenticated
// boot requests (/users/me, /users/permissions, FetchShops) in PARALLEL at app init so the
// serial route-guard chain resolves from a warm/in-flight cache. Returns void => never
// blocks bootstrap. No-ops on unauthenticated/expired/demo sessions (see the service).
export function initBootstrapPreload(preloadService: BootstrapPreloadService) {
  return () => preloadService.preload();
}
@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    RouterModule,
    SharedModule,
    OverlayModule,
    LoadingBarModule,
    BrowserAnimationsModule,
    AuthModule,
    PlansModule,
    AuthShopModule,
    ModuleAccessDirective,
    CanEditDirective,
    NgxsModule.forRoot([
      AuthState,
      OrderState,
      InventoryState,
      ShopState,
      CollaboratorState,
      MetricsWidgetConfigState,
      UserState
    ]),
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true
    }),
    TranslocoModule,
    KbChatbotComponent
  ],
  providers: [
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false // Disable dark mode detection until dark theme is implemented
        }
      }
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initDemoRouting,
      deps: [DemoRoutingService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initBootstrapPreload,
      deps: [BootstrapPreloadService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DemoCheckInterceptor,
      multi: true
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideTransloco({
      config: {
        availableLangs: ['en', 'en-GB', 'de', 'fr', 'it', 'es'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: environment.production
      },
      loader: TranslocoHttpLoader
    })
  ]
})
export class AppModule {}
